import { orientToPage, useCurrentPageTitle } from '@athena/ui'
import { useRouter } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

/** The history moves that return to a page already seen, rather than going somewhere new. */
const RETURNS: ReadonlySet<string> = new Set(['BACK', 'FORWARD', 'GO'])

/**
 * Orients the user after a real page change (`orientToPage` says where
 * focus goes and why).
 *
 * "Real" is a new path: a filter or a search changes only the query string,
 * and the user is still on the same screen with focus where they left it. The
 * first page of a visit is left to the browser, which is already on it.
 *
 * It waits for the new page to name itself before moving focus, so the
 * heading it lands on already says the new page's name rather than the last
 * one's; a page that names itself late, or keeps the same name, is oriented a
 * moment later anyway.
 */
/** What a navigation has left to do, held across renders without causing one. */
interface Orientation {
  pending: 'navigated' | 'restored' | null
  lastMove: string | null
  fallback: ReturnType<typeof setTimeout> | undefined
}

/** Orient to the page once, if a navigation is still waiting for it. */
function settle(state: Orientation) {
  const how = state.pending
  if (!how) return
  state.pending = null
  clearTimeout(state.fallback)
  requestAnimationFrame(() => orientToPage(how))
}

export function RouteOrientation() {
  const router = useRouter()
  const title = useCurrentPageTitle()
  const state = useRef<Orientation>({ pending: null, lastMove: null, fallback: undefined }).current

  useEffect(
    () =>
      router.history.subscribe(({ action }) => {
        state.lastMove = action.type
      }),
    [router, state],
  )

  useEffect(
    () =>
      router.subscribe('onResolved', (event) => {
        if (!event.fromLocation || !event.pathChanged) return
        state.pending = RETURNS.has(state.lastMove ?? '') ? 'restored' : 'navigated'
        state.lastMove = null
        clearTimeout(state.fallback)
        state.fallback = setTimeout(() => settle(state), 400)
      }),
    [router, state],
  )

  // The new page has said its name, so its heading says it too.
  useEffect(() => {
    if (title) settle(state)
  }, [title, state])

  useEffect(() => () => clearTimeout(state.fallback), [state])

  return null
}
